import { useStudentGroupsQuery } from "@/modules/master/presentation/api/queries";
import {
  CreateStudentSchema,
  GENDER_VALUES,
} from "@/modules/students/domain/contracts";
import { capitalizeEnglishNameWords } from "@/modules/students/domain/lib/capitalize-english-name";
import { isDevMode } from "@/shared/lib/config";
import { AvatarPresignedUpload } from "@/shared/ui/AvatarPresignedUpload";
import {
  Button,
  Checkbox,
  FormDateField,
  FormInput,
  FormRoot,
  FormSelect,
  FormTextarea,
  Label,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import { useMemo } from "react";
import type { z } from "zod";

const StudentFormSchema = CreateStudentSchema;
export type StudentFormValues = z.infer<typeof StudentFormSchema>;

export function StudentForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<StudentFormValues & { memberId?: string }>;
  onSubmit: (values: StudentFormValues) => void;
  submitting?: boolean;
}) {
  const methods = RHF.useForm<StudentFormValues>({
    // @ts-expect-error - zodResolver type mismatch with default() in schema
    resolver: zodResolver(StudentFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      nickname: initialValues?.nickname || "",
      phoneNumber: initialValues?.phoneNumber || "",
      email: initialValues?.email || "",
      dateOfBirth: initialValues?.dateOfBirth || undefined,
      gender: initialValues?.gender ?? undefined,
      status: (initialValues?.status as "active" | "deactivated") || "active",
      notes: initialValues?.notes || "",
      image: initialValues?.image ?? undefined,
      groupId: initialValues?.groupId || undefined,
      whatsappOptIn: initialValues?.whatsappOptIn ?? false,
    },
  });

  // Query active student groups for dropdown
  const { data: groupsData } = useStudentGroupsQuery({
    limit: 100,
    offset: 0,
    filters: [{ field: "status", op: "eq", value: "active" }],
  });

  const groupOptions = useMemo(() => {
    const groups = groupsData?.data ?? [];
    return [
      { value: null, label: "ບໍ່ມີກຸ່ມ" },
      ...groups.map((g) => ({
        value: g.id,
        label: `${g.name} (${g.code})`,
      })),
    ];
  }, [groupsData]);

  const selectedGroup = useMemo(() => {
    const groupId = methods.watch("groupId");
    if (!groupId) return null;
    return groupsData?.data?.find((g) => g.id === groupId);
  }, [methods.watch("groupId"), groupsData]);

  const handleAutoFill = () => {
    methods.reset({
      name: "ນາງ ສຸດາ ວົງສະວັດ",
      phoneNumber: "020 12345678",
      email: "suda@example.com",
      dateOfBirth: "2010-05-15",
      gender: "female",
      status: "active",
      notes: "ນັກຮຽນໃໝ່",
    });
  };

  const handleSubmit = (values: StudentFormValues) => {
    const submitValues = {
      ...values,
      name: capitalizeEnglishNameWords(values.name),
      nickname: values.nickname?.trim()
        ? capitalizeEnglishNameWords(values.nickname)
        : null,
      groupId: values.groupId === "" ? null : values.groupId,
      dateOfBirth:
        values.dateOfBirth === "" || values.dateOfBirth == null
          ? null
          : values.dateOfBirth,
      gender:
        values.gender === "" || values.gender == null ? null : values.gender,
    };
    onSubmit(submitValues);
  };

  return (
    <FormRoot methods={methods} onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {initialValues?.memberId && (
          <div className="space-y-2">
            <label className="font-medium text-sm" htmlFor="memberId">
              Member ID
            </label>
            <input
              type="text"
              value={initialValues.memberId}
              disabled
              readOnly
              className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        )}

        <AvatarPresignedUpload
          value={methods.watch("image")}
          onChange={(key) => methods.setValue("image", key ?? undefined)}
          label="ຮູບໂປຣໄຟລ໌"
          hint="ອັບໂຫຼດຮູບໃໝ່ (ອັບໂຫຼດໄປ MinIO ໂດຍກົງ)"
        />

        <FormInput
          name="name"
          label="ຊື່-ນາມສະກຸນ"
          placeholder="ປ້ອນຊື່-ນາມສະກຸນ"
          required
          onChange={(event) => {
            const input = event.currentTarget;
            const selectionStart = input.selectionStart;
            const selectionEnd = input.selectionEnd;
            const next = capitalizeEnglishNameWords(input.value);
            methods.setValue("name", next, {
              shouldDirty: true,
              shouldValidate: true,
            });
            if (next !== input.value) {
              input.value = next;
              if (selectionStart != null && selectionEnd != null) {
                input.setSelectionRange(selectionStart, selectionEnd);
              }
            }
          }}
        />
        <p className="-mt-2 text-muted-foreground text-xs">
          ຊື່ຈິງເທົ່ານັ້ນ — ຢ່າໃສ່ວົງເລັບຊື່ຫຼີ້ນໃນຊ່ອງນີ້
        </p>

        <FormInput
          name="nickname"
          label="ຊື່ຫຼີ້ນ"
          placeholder="ບໍ່ບັງຄັບ"
          onChange={(event) => {
            const input = event.currentTarget;
            const next = capitalizeEnglishNameWords(input.value);
            methods.setValue("nickname", next, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormInput
            name="phoneNumber"
            label="ເບີໂທ"
            placeholder="ປ້ອນເບີໂທ (ບໍ່ບັງຄັບ)"
          />

          <FormInput
            name="email"
            label="ອີເມວ"
            type="email"
            placeholder="ປ້ອນອີເມວ (ບໍ່ບັງຄັບ)"
          />
        </div>

        <div className="flex items-center gap-2 rounded-md border p-3">
          <Checkbox
            id="whatsappOptIn"
            checked={Boolean(methods.watch("whatsappOptIn"))}
            onCheckedChange={(checked) =>
              methods.setValue("whatsappOptIn", checked === true)
            }
          />
          <Label htmlFor="whatsappOptIn" className="cursor-pointer text-sm">
            ຍິນຍອມຮັບແຈ້ງເຕືອນ WhatsApp ກ່ຽວກັບແພັກເກດ
          </Label>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormDateField name="dateOfBirth" label="ວັນເດືອນປີເກີດ" />
          <FormSelect
            name="gender"
            label="ເພດ"
            options={[
              { value: "other", label: "ບໍ່ລະບຸ" },
              ...GENDER_VALUES.map((v) => ({
                value: v,
                label: v === "male" ? "ຊາຍ" : v === "female" ? "ຍິງ" : "ອື່ນໆ",
              })),
            ]}
          />
        </div>

        <FormTextarea
          name="notes"
          label="ໝາຍເຫດ"
          placeholder="ປ້ອນໝາຍເຫດ (ບໍ່ບັງຄັບ)"
          rows={3}
        />

        <div className="space-y-2">
          <FormSelect label="ກຸ່ມນັກຮຽນ" name="groupId" options={groupOptions} />
          {selectedGroup && (
            <p className="text-muted-foreground text-xs">
              Member ID ຈະເປັນ: {selectedGroup.code}-000001
            </p>
          )}
          {!selectedGroup && !methods.watch("groupId") && (
            <p className="text-muted-foreground text-xs">
              Member ID ຈະເປັນ: ST000001
            </p>
          )}
        </div>

        <FormSelect
          label="ສະຖານະ"
          name="status"
          options={[
            { value: "active", label: "ໃຊ້ງານ" },
            { value: "deactivated", label: "ປິດການໃຊ້ງານ" },
          ]}
        />
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
